import {mix, Loadable, Eventable} from '../mixin';
import Gainable from '../mixin/Gainable';
import audioCore from '../service/audioCore'; // eslint-disable-line
import Audio from './Audio';
import AjaxRequest from './AjaxRequest';
import CallbackChain from './CallbackChain';
import randomizer from '../service/randomizer';

const format = 'mp3';

class AudioController extends mix().with(Loadable, Eventable, Gainable)
{
    static needsTouchInit()
    {
        console.warn(
            'Will always return "true" because every device needs user interaction for unlocking.'
        );
        return true;
    }

    static isInitialized()
    {
        console.warn('Deprecated. Please use "audioCore.unlocked" or "audioCore.unlockedPromise".');
        return audioCore.unlocked;
    }

    // eslint-disable-next-line consistent-return
    static suspendUntilInteraction(suspend)
    {
        console.warn('Deprecated. Please use "audioCore.unlockQueueActive" getter & setter.');
        if (suspend === undefined)
        {
            return audioCore.unlockQueueActive;
        }
        audioCore.unlockQueueActive = suspend;
    }

    static clearChannel(name)
    {
        console.warn('Deprecated. Please use "audioCore::clearChannel".');
        audioCore.clearChannel(name);
    }

    static isChannelPlaying(name)
    {
        console.warn('Deprecated. Please use "audioCore::isChannelPlaying".');
        return audioCore.isChannelPlaying(name);
    }

    static setChannel(name, audio)
    {
        console.warn('Deprecated. Please use "audioCore::setChannel".');
        audioCore.setChannel(name, audio);
    }

    static clearAllChannels()
    {
        console.warn('Deprecated. Please use "audioCore::clearAllChannels".');
        audioCore.clearAllChannels();
    }

    static mute(bool = true)
    {
        console.warn('Deprecated. Please use "audioCore.muted" setter.');
        audioCore.muted = bool;
    }

    static setVolume(volume)
    {
        console.warn('Deprecated. Please use "audioCore.volume" setter.');
        audioCore.volume = volume;
    }

    static setFormats()
    {
        console.warn('Deprecated. For now only mp3 is supported!');
    }

    static get(namespace)
    {
        console.warn('Deprecated. Please use "audioCore.getController".');
        return audioCore.getController(namespace);
    }

    static getAll()
    {
        console.warn('Deprecated. Please use "audioCore.getAllControllers".');
        return audioCore.getAllControllers();
    }

    #basePath = '';
    #namespace = '';
    #assets = [];
    #buffers = [];
    #audioConfigs = {};
    #atmos = {};
    #loaded = false;

    /**
     * @mixes Gainable
     * @mixes Loadable
     * @mixes Eventable
     * @borrows Gainable#muted as #muted
     * @borrows Gainable#volume as #volume
     * @borrows Gainable#gainNode as #gainNode
     * @borrows Gainable#fadeTween as #fadeTween
     * @borrows Gainable#fade as #fade
     * @see audioCore
     * @hideconstructor
     */
    constructor(namespace = null, basePath = './', createdByCore = false)
    {
        super();

        if (!createdByCore)
        {
            console.warn(
                'Please use "audioCore::createController" to create a new AudioController.'
            );
        }

        this.#namespace = namespace || randomizer.createUUID();
        this.basePath = basePath;
        audioCore._setController(this.#namespace, this);

        /**
         * @event AudioController#unlock
         */
        audioCore.unlockedPromise.then(() => this.fire('unlock'));
    }

    get static()
    {
        console.warn(
            'Deprecated. All static methods are now in "audioCore". Use ".core" getter.'
        );
        return AudioController;
    }

    /** Getter
     * @type {audioCore}
     */
    get core()
    {
        return audioCore;
    }

    /** Setter
     * @type {string}
     */
    set basePath(basePath)
    {
        if (basePath.slice(-1) !== '/') { basePath += '/'; }
        this.#basePath = basePath;
    }

    /** Getter
     * @type {boolean}
     */
    get hasManifest()
    {
        return !!this.#assets.length;
    }

    /** Getter
     * @type {string}
     */
    get namespace()
    {
        return this.#namespace;
    }

    getNamespace()
    {
        console.warn('Deprecated. Use the ".namespace" getter.');
        return this.#namespace;
    }

    /** Das Manifest ist ein Array, welches aus Strings oder Objekten besteht. Bei Strings wird davon ausgegangen, dass die Id
     * des Audios auch der Pfad zur Datei ist (ohne Endung). Wenn man ein Objekt übergibt, kann man die Attribute ``id`` und
     * ``src`` (kompletter Pfad ab basePath oder base64-Source) getrennt angeben. Und außerdem noch ein ``sprites`` Objekt,
     * falls es sich beim Audio um ein Spritesheet handelt.
     * @param {Array} manifest
     * @return {AudioController}
     */
    loadManifest(manifest)
    {
        if (this.hasManifest)
        {
            throw new Error('You can only add one manifest per AudioController instance.');
        }
        manifest.forEach(asset => this.#createAsset(asset));

        // last chance to trigger creation of context before breaking code without it
        // eslint-disable-next-line no-unused-expressions
        audioCore.context;
        this.#loadAssets();

        if (this.hasManifest)
        {
            this.gainNode.connect(audioCore.gainNode);
        }
        return this;
    }

    #createAsset(asset)
    {
        if (typeof asset === 'string')
        {
            asset = {id: asset, src: this.createAudioSrcPath(asset)};
        }
        // altes Format
        else if (asset.path)
        {
            asset.src = this.createAudioSrcPath(asset.path);
            if (!asset.id)
            {
                asset.id = asset.path;
            }
        }
        // neues Format: kein src, aber id
        else if (asset.id && !asset.src)
        {
            asset.src = this.createAudioSrcPath(asset.id);
        }
        // neues Format: kein base64 und src mit Dateiendung
        else if (!(/^data:[^;]+;base64,/.test(asset.src)))
        {
            asset.src = this.createAudioSrcPath(asset.src, false);
        }

        if (asset.src && asset.id)
        {
            this.#assets.push(asset);
        }
        else
        {
            console.warn('Manifest asset has wrong format: ', asset);
        }
    }

    #loadAssets()
    {
        const {length} = this.#assets;
        if (length === 0)
        {
            this.addLoad().completeLoad();
            return;
        }
        this.addLoad(length);
        this.#assets.forEach((asset) =>
        {
            const bufferId = randomizer.createUUID();
            this.#buffers.push(bufferId);
            new AjaxRequest(asset.src).expectArrayBuffer().get().then((arrayBuffer) =>
            {
                if (!this.#loaded) { return; }
                const {context} = audioCore;
                const onComplete = (buffer) =>
                {
                    audioCore._setBuffer(bufferId, buffer);
                    this.completeLoad();
                };

                // @TODO wahrscheinlich eh Quatsch, aber von Howler übernommen
                if (typeof Promise !== 'undefined' && context.decodeAudioData.length === 1)
                {
                    context.decodeAudioData(arrayBuffer).then(onComplete);
                }
                else
                {
                    context.decodeAudioData(arrayBuffer, onComplete);
                }
            });

            if (!asset.sprites)
            {
                this.#audioConfigs[asset.id] = {
                    buffer: bufferId,
                    id: asset.id,
                    namespace: this.#namespace,
                    destinationNode: this.gainNode
                };
            }
            else
            {
                for (const [id, sprite] of Object.entries(asset.sprites))
                {
                    this.#audioConfigs[id] = {
                        buffer: bufferId,
                        id,
                        namespace: this.#namespace,
                        offset: sprite[0] / 1000,
                        duration: sprite[1] / 1000,
                        destinationNode: this.gainNode
                    };
                }
            }
        });
        this.#loaded = true;
    }

    createAudioSrcPaths(path)
    {
        console.warn('Deprecated. Use "::createAudioSrcPath".');
        return [this.createAudioSrcPath(path)];
    }

    /** Erstellt anhand des Formats und des basePaths aus einem Pfad mit oder ohne Dateiendung einen
     * vollständigen Audio-Datei-Pfad.
     * @param {string} path
     * @param {boolean} [addExtension=true]
     * @return {string} Vollständiger Pfad zur Audio-Datei.
     */
    createAudioSrcPath(path, addExtension = true)
    {
        const srcPath = `${this.#basePath}${path}`;
        if (addExtension)
        {
            return `${srcPath}.${format}`;
        }
        return srcPath;
    }

    /** Existiert die Audio-ID im Controller?
     * @param {string} id
     * @return {boolean}
     */
    hasAudio(id)
    {
        return Object.hasOwn(this.#audioConfigs, id);
    }

    getAudioEntry(id)
    {
        console.warn('Deprecated. Use "::getAudioConfig".');
        return this.getAudioConfig(id);
    }

    /** Sucht anhand der Id im Register nach dem Audio-Eintrag. Wenn es keinen findet, wird das Event audioNotFound gefeuert.
     * Wenn kein anhängter Handler den Entry setzt, wird noch in anderen Controllern geschaut, falls die Id dem Format
     * ``otherNamespace.audio1`` entspricht.
     * @param {string} id
     * @return {Object=}
     */
    getAudioConfig(id)
    {
        let audioConfig = this.#audioConfigs[id] || null;
        if (!audioConfig)
        {
            const eventObj = {
                id,
                setAudioEntry: (entry) =>
                {
                    console.warn('Deprecated. Use "event::setAudioConfig".');
                    audioConfig = entry;
                },
                setAudioConfig: (config) => { audioConfig = config; },
                controller: this,
                getController: audioCore.getController
            };

            this.fire('audioNotFound', eventObj);
        }

        if (!audioConfig)
        {
            const splittedId = id.split('.');
            if (splittedId.length === 2)
            {
                const altController = audioCore.getController(splittedId[0]);
                if (altController)
                {
                    audioConfig = altController.getAudioConfig(splittedId[1]);
                }
            }
        }

        if (!audioConfig)
        {
            throw ReferenceError(`Audio with id "${id}" not found.`);
        }

        return audioConfig || null;
    }

    /** Liefert anhand der Id eine Audio-Instanz zurück.
     * @param {string} id
     * @return {Audio}
     */
    getAudio(id)
    {
        return new Audio(this.getAudioConfig(id));
    }

    /** Spielt anhand der Id ein Audio ab und liefert dessen Instanz zurück.
     * @param {string} id
     * @param {Audio~AudioOptions} options
     * @return {Audio}
     */
    play(id, options = {})
    {
        return this.getAudio(id).play(options);
    }

    /** Spielt Audios hintereinander ab. Wenn ein Audio abgespielt wurde, wird es entladen. Verwendet die
     * ``delay`` Option des Audios. Wird ein Channel übergeben, ist es möglich, die Queue über
     * {@link audioCore.clearChannel clearChannel} zu stoppen.
     * @param {Array.<string>} ids Array von Ids
     * @param {Audio~AudioOptions} options
     * @return {Audio[]}
     */
    playQueue(ids, options = {})
    {
        const chain = new CallbackChain();
        const {length} = ids;
        const audios = [];

        const {queueGap} = options;
        let gaps = null;
        if (Array.isArray(queueGap))
        {
            gaps = queueGap;
        }
        else
        {
            const gap = typeof queueGap === 'number' ? queueGap : undefined;
            gaps = Array(length).fill(gap);
            gaps[0] = undefined;
        }

        delete options.queueGap;
        delete options.loops;

        for (let i = 0; i < length; i++)
        {
            const audio = this.getAudio(ids[i]);
            const delay = gaps[i];
            chain.add(() =>
            {
                audio
                    .once('end', function()
                    {
                        this.unload();
                        chain.next();
                    })
                    .play({...options, delay});
            });
            audios.push(audio);
        }

        chain.next();
        return audios;
    }

    /** Stoppt alle Audios (pausiert oder spielend), inklusive Atmos und delayed Audios.
     * @return {AudioController}
     */
    stopAll()
    {
        audioCore.stopAll(this.#namespace);
        return this;
    }

    /** Pausiert alle aktiven Audios, inklusive Atmos und delayed Audios.
     * @return {AudioController}
     */
    pauseAll()
    {
        audioCore.pauseAll(this.#namespace);
        return this;
    }

    /** Setzt alle zuvor pausierten Audios wieder fort.
     * @return {AudioController}
     */
    resumeAll()
    {
        audioCore.resumeAll(this.#namespace);
        return this;
    }

    /** Entlädt alle Audios. Controller kann weiter benutzt werden. Wenn das Manifest nicht entladen wurde,
     * lann man über reload die Audios erneut laden.
     * @return {AudioController}
     * @param {boolean} [includeManifest=false]
     */
    unload(includeManifest = false)
    {
        if (!this.hasManifest) { return this; }

        this.stopAtmos();
        audioCore.clearChannelsByNamespace(this.#namespace);
        audioCore.unloadAll(this.#namespace);
        this.#buffers.forEach(bufferId => audioCore._setBuffer(bufferId, null));
        this.#buffers.length = 0;

        if (includeManifest)
        {
            this.unloadManifest();
        }
        this.#loaded = false;
        this.resetLoadProgress();
        this.gainNode.disconnect();
        return this;
    }

    /**
     * {@link AudioController#reload reload} ist dann nicht mehr möglich.
     * @return {AudioController}
     */
    unloadManifest()
    {
        this.#assets.length = 0;
        return this;
    }

    /** Lädt Audios neu, wenn der Controller entladen wurde. */
    reload()
    {
        if (!this.#loaded && this.hasManifest)
        {
            this.#loadAssets();
            this.gainNode.connect(audioCore.gainNode);
        }
        return this;
    }

    /** Entlädt alle Audios und entfernt Controller aus dem {@link audioCore}. */
    destroy()
    {
        this.unload(true);
        audioCore._setController(this.#namespace, null);
    }

    /** Spielt Atmo-Audio (loopender Hintergrund-Sound) ab, welche getrennt von den anderen Audios behandelt werden können.
     * @param {string} id
     * @param {Audio~AudioOptions} options
     * @return {AudioController}
     */
    playAtmo(id, options = {})
    {
        const atmo = this.getAtmo(id) || this.getAudio(id);
        options.loops = -1;
        this.#atmos[id] = atmo.play(options);
        return this;
    }

    /** Pausiert ein vorhandenes Atmo-Audio.
     * @param {string} id
     * @return {AudioController}
     */
    pauseAtmo(id)
    {
        const atmo = this.getAtmo(id);
        if (atmo) { atmo.pause(); }
        return this;
    }

    /** Stoppt ein vorhandenes Atmo-Audio und entfernt es aus dem Speicher.
     * @param {string} id
     * @return {AudioController}
     */
    stopAtmo(id)
    {
        const atmo = this.getAtmo(id);
        if (atmo)
        {
            atmo.unload();
            delete this.#atmos[id];
        }
        return this;
    }

    /** Stoppt alle Atmo-Audios.
     * @return {AudioController}
     */
    stopAtmos()
    {
        for (const id in this.#atmos)
        {
            this.stopAtmo(id);
        }
        return this;
    }

    /** Liefert ein Atmo-Audio mit dieser Id zurück, falls es existiert.
     * @param {string} id
     * @return {?Audio}
     */
    getAtmo(id)
    {
        return this.#atmos[id] || null;
    }

    clearChannel(name)
    {
        console.warn('Deprecated. Use ".core::clearChannel".');
        audioCore.clearChannel(name);
        return this;
    }

    isChannelPlaying(name)
    {
        console.warn('Deprecated. Use ".core::isChannelPlaying".');
        return audioCore.isChannelPlaying(name);
    }

    on(types, handler, options)
    {
        if (types === 'touchInit')
        {
            console.warn('Event "touchInit" was renamed to "unlock".');
            types = 'unlock';
        }
        super.on(types, handler, options);
        return this;
    }
}

export default AudioController;
